/* Discount break-even calculator (/tools/discount-calculator).
   Pure arithmetic on the merchant's own numbers: what one order keeps before
   and after a discount, and how many more orders the discount needs to make
   the same profit. It never estimates how many more people will buy: that
   depends on the shoppers, and the page says so.
   One money model everywhere on the page: profit per order = order value
   (what shoppers pay for the items, before tax and shipping) − cost per
   order (product, packing and payment fees, plus any shipping cost shoppers
   don't pay for). So free shipping costs exactly the shipping shoppers pay
   now, and every discount comes whole out of the profit.
   Money is held in whole cents and percentages in basis points (10% = 1000),
   so $48 × 10% is exactly $4.80 and no float residue reaches the screen. */
(function (root, factory) {
  'use strict';
  const calculator = factory();
  const commonJS = typeof module === 'object' && module && module.exports;
  if (commonJS) module.exports = calculator;
  else if (root) root.DiscountCalculator = calculator;
  const doc = !commonJS && root ? root.document : null;
  if (doc) {
    const start = () => calculator.init(doc.querySelector('[data-dbc]'));
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // $1,000,000 and 1,000,000 orders: far past any real order, and small
  // enough that orders × cents (at most 10^14) stays an exact integer.
  const MAX_CENTS = 100000000;
  const MAX_ORDERS = 1000000;
  const MODES = Object.freeze(['percent', 'amount', 'shipping']);
  // The worked example: Inbox Arena's $48 face wash, which costs $18 for the
  // product, packing and payment fees; the shopper pays the shipping. (The
  // ROI guide works a different example: $48 orders with $24 of costs.)
  const DEFAULTS = Object.freeze({ price: '48', cost: '18', mode: 'percent', percent: '10', amount: '5', shipping: '6', orders: '100' });
  const STEPS_BPS = Object.freeze([500, 1000, 1500, 2000, 2500, 3000]);
  const FIELDS = Object.freeze(['price', 'cost', 'percent', 'amount', 'shipping', 'orders']);
  // How each box is read: the decimal places kept, the unit it may carry and
  // its limits. "price" is the order value; the key stays for links (?price=).
  const RULES = Object.freeze({
    price: { places: 2, unit: '$', min: 1, max: MAX_CENTS },
    cost: { places: 2, unit: '$', min: 0, max: MAX_CENTS },
    percent: { places: 2, unit: '%', min: 0, max: 10000 },
    amount: { places: 2, unit: '$', min: 0, max: MAX_CENTS },
    shipping: { places: 2, unit: '$', min: 0, max: MAX_CENTS },
    orders: { places: 0, unit: '', min: 1, max: MAX_ORDERS, optional: true }
  });
  // Round "$ off" amounts for the comparison, in cents: 25¢, 50¢, $1, $2, $3,
  // $5, then 1, 1.5, 2, 2.5, 3, 4, 5 and 7.5 times each power of ten from $10.
  const AMOUNT_LADDER = Object.freeze((() => {
    const ladder = [25, 50, 100, 200, 300, 500];
    for (let power = 1000; power <= MAX_CENTS; power *= 10) {
      for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 7.5]) ladder.push(step * power);
    }
    return ladder;
  })());
  // A result that doesn't work yet (a half-typed "1" of "120") waits this long
  // after the last keystroke before it replaces the last one that did.
  const SETTLE_MS = 600;
  const MINUS = '−';
  const NBSP = ' ';
  const MESSAGES = Object.freeze({
    price: { blank: 'Enter an order value.', format: 'Enter a number, like 48 or 48.50.', unit: 'Enter a dollar amount, like 48.', low: 'Enter an order value above $0.', high: 'Enter an order value up to $1,000,000.' },
    cost: { blank: 'Enter your cost per order, or 0.', format: 'Enter a number, like 18 or 18.50.', unit: 'Enter a dollar amount, like 18.', low: 'Your cost can’t be below $0.', high: 'Enter a cost up to $1,000,000.' },
    percent: { blank: 'Enter a discount, like 10.', format: 'Enter a number, like 10 or 12.5.', unit: 'For a dollar amount, choose “$ off” above.', low: 'A discount can’t be below 0%.', high: 'A discount can’t be more than 100%.' },
    amount: { blank: 'Enter the amount off, like 5.', format: 'Enter a number, like 5 or 4.50.', unit: 'For a percentage, choose “% off” above.', low: 'A discount can’t be below $0.', high: 'Enter an amount up to $1,000,000.' },
    shipping: { blank: 'Enter what shoppers pay for shipping, like 6.', format: 'Enter a number, like 6 or 5.99.', unit: 'Enter a dollar amount, like 6.', low: 'Shipping can’t be below $0.', high: 'Enter an amount up to $1,000,000.' },
    orders: { format: 'Enter a whole number, like 100.', unit: 'Enter a number of orders, like 100.', fraction: 'Use a whole number of orders, like 100.', low: 'Enter 1 or more, or leave it blank.', high: 'Enter up to 1,000,000 orders.' }
  });
  // How the results panel names a field it is waiting for: [blank, invalid].
  const NOUNS = Object.freeze({
    price: ['an order value', 'the order value'],
    cost: ['your cost per order', 'your cost per order'],
    percent: ['a discount', 'the discount'],
    amount: ['a discount', 'the discount'],
    shipping: ['what shoppers pay for shipping', 'the shipping amount']
  });

  const group = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const count = (n, one, many = one + 's') => `${group(String(n))} ${n === 1 ? one : many}`;

  function formatMoney(cents) {
    const abs = Math.abs(cents);
    const rest = abs % 100;
    return (cents < 0 ? MINUS : '') + '$' + group(String((abs - rest) / 100)) + (rest ? '.' + String(rest).padStart(2, '0') : '');
  }

  // 1000 → "10%", 1250 → "12.5%", 333 → "3.33%".
  function formatBps(bps) {
    const rest = bps % 100;
    return group(String((bps - rest) / 100)) + (rest ? '.' + String(rest).padStart(2, '0').replace(/0$/, '') : '') + '%';
  }

  // Derived percentages, never NaN or Infinity on screen: whole numbers from
  // 10% ("19%", "400%"), one decimal below that ("8.7%", and "5%", never
  // "5.0%"), two significant figures for tiny values.
  function formatPercent(value) {
    if (!Number.isFinite(value) || value < 0) return '';
    if (value === 0) return '0%';
    if (value < 0.001) return 'under 0.001%';
    if (value < 0.1) return String(Number(value.toPrecision(2))) + '%';
    const tenths = Math.round(value * 10);
    return (tenths >= 100 ? group(String(Math.round(value))) : String(tenths / 10)) + '%';
  }

  // 1087 at 3 places → "1.087", 120 at 2 → "1.2", 30000 at 2 → "300".
  function decimalText(units, places) {
    const scale = 10 ** places;
    const rest = units % scale;
    return group(String((units - rest) / scale)) + (rest ? '.' + String(rest).padStart(places, '0').replace(/0+$/, '') : '');
  }

  // "$30 ÷ $25.20 = 1.19, so 19% more": the ratio is given the precision of
  // the percentage it turns into, so the two numbers always agree.
  function formatRatio(extraPercent) {
    if (!Number.isFinite(extraPercent) || extraPercent < 0) return '';
    if (extraPercent === 0) return '1';
    if (extraPercent < 0.001) return 'just over 1';
    if (extraPercent < 0.1) return String(Number((1 + Number(extraPercent.toPrecision(2)) / 100).toPrecision(8)));
    const tenths = Math.round(extraPercent * 10);
    return tenths < 100 ? decimalText(1000 + tenths, 3) : decimalText(100 + Math.round(extraPercent), 2);
  }

  // Reads what people type into a box: "48", "$48.50", "1,200", "12.5%".
  // A single comma followed by up to two digits is a decimal comma ("48,50"
  // from a phone keypad set to a comma), because a thousands comma is always
  // followed by three. Anything else is refused, never guessed. The value is
  // returned in whole units of 10^-places, rounded half up.
  function parseDecimal(raw, places, unit) {
    let text = String(raw == null ? '' : raw).replace(/\s+/g, '');
    let negative = false;
    const stripUnit = () => { if (unit === '$') text = text.replace(/^\$/, ''); };
    stripUnit();
    if (/^[-−]/.test(text)) { negative = true; text = text.slice(1); stripUnit(); }
    if (unit === '%') text = text.replace(/%$/, '');
    if (!text || text === '.') return { status: 'blank' };
    if (/[$%]/.test(text)) return { status: 'unit' };
    if (text.includes(',')) {
      if (/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(text)) text = text.replace(/,/g, '');
      else if (/^\d+,\d{0,2}$/.test(text)) text = text.replace(',', '.');
      else return { status: 'format' };
    }
    if (!/^(\d+\.?\d*|\.\d+)$/.test(text)) return { status: 'format' };
    const [whole, fraction = ''] = text.split('.');
    const digits = whole.replace(/^0+/, '');
    if (digits.length > 10) return { status: 'large', negative };
    const padded = (fraction + '0'.repeat(places + 1)).slice(0, places + 1);
    let value = Number(digits || 0) * 10 ** places + Number(padded.slice(0, places) || 0);
    if (Number(padded[places]) >= 5) value += 1;
    return { status: 'ok', value: negative && value ? -value : value, exact: !/[1-9]/.test(fraction.slice(places)) };
  }

  function readField(key, raw, limits) {
    const { places, unit, min, max, optional = false, highMessage } = Object.assign({}, RULES[key], limits);
    const messages = MESSAGES[key];
    const fail = (kind, message) => ({ error: { kind, message: message || messages[kind] || messages.format } });
    const parsed = parseDecimal(raw, places, unit);
    if (parsed.status === 'blank') return optional ? { value: null } : fail('blank');
    if (parsed.status === 'unit') return fail('unit');
    if (parsed.status === 'format') return fail('format');
    if (parsed.status === 'large') return parsed.negative ? fail('low') : fail('high', highMessage);
    if (places === 0 && !parsed.exact) return fail('fraction');
    if (parsed.value < min) return fail('low');
    if (parsed.value > max) return fail('high', highMessage);
    return { value: parsed.value };
  }

  // Taking more than the order value off would pay the shopper to buy.
  const amountLimit = (priceCents) => (priceCents === null ? {} : { max: priceCents, highMessage: `The discount can’t be more than the ${formatMoney(priceCents)} order value.` });
  const percentOf = (priceCents, bps) => Math.round(priceCents * bps / 10000);

  // Turns the raw box values into cents, basis points and whole orders, with
  // a message for each box that cannot be used. Only the active discount box
  // counts; the other two keep their values for when the shopper switches back.
  function readInputs(values) {
    const source = values || {};
    const mode = MODES.includes(source.mode) ? source.mode : 'percent';
    const errors = {};
    const take = (key, result) => {
      if (result.error) errors[key] = result.error;
      return result.error ? null : result.value;
    };
    const priceCents = take('price', readField('price', source.price));
    const costCents = take('cost', readField('cost', source.cost));
    let percentBps = null;
    let discountCents = null;
    if (mode === 'percent') {
      percentBps = take('percent', readField('percent', source.percent));
      if (percentBps !== null && priceCents !== null) discountCents = percentOf(priceCents, percentBps);
    } else if (mode === 'amount') {
      discountCents = take('amount', readField('amount', source.amount, amountLimit(priceCents)));
    } else {
      // Free shipping may cost more than a cheap order's value; only the sanity cap applies.
      discountCents = take('shipping', readField('shipping', source.shipping));
    }
    const orders = take('orders', readField('orders', source.orders));
    const valid = priceCents !== null && costCents !== null && discountCents !== null;
    return { mode, priceCents, costCents, percentBps, discountCents, orders, errors, valid };
  }

  // A box's value once the visitor leaves it: "$48" → "48", "1,200" → "1200",
  // "48,50" → "48.50", "12.5%" → "12.5". Anything that doesn't read as a
  // number stays exactly as typed, next to its message.
  function tidyValue(key, raw) {
    const text = String(raw == null ? '' : raw);
    const rule = RULES[key];
    if (!rule) return text;
    const parsed = parseDecimal(text, rule.places, rule.unit);
    if (parsed.status !== 'ok' || (rule.places === 0 && !parsed.exact)) return text;
    const sign = parsed.value < 0 ? '-' : '';
    const abs = Math.abs(parsed.value);
    if (!rule.places) return sign + String(abs);
    const rest = abs % 100;
    const cents = String(rest).padStart(2, '0');
    return sign + String((abs - rest) / 100) + (rest ? '.' + (key === 'percent' ? cents.replace(/0$/, '') : cents) : '');
  }

  // Numbers a link can fill in: ?price=60&cost=35&percent=15, or amount= or
  // shipping= (either also picks that discount type), and orders=. A value
  // is used only if its box would accept it, tidied as the box would tidy it;
  // anything else is ignored. The first usable discount picks the type.
  function readQuery(search, base) {
    const values = {};
    const discounts = [];
    let params;
    try {
      params = new URLSearchParams(String(search || ''));
    } catch (_) {
      return { mode: null, values };
    }
    const seen = new Set();
    for (const [key, raw] of params) {
      if (!FIELDS.includes(key) || seen.has(key)) continue;
      seen.add(key);
      const result = readField(key, raw);
      if (result.error || result.value === null) continue;
      values[key] = tidyValue(key, raw);
      if (MODES.includes(key)) discounts.push(key);
    }
    if (values.amount !== undefined) {
      const price = readField('price', values.price !== undefined ? values.price : (base || DEFAULTS).price);
      if (price.error || readField('amount', values.amount, amountLimit(price.value)).error) {
        delete values.amount;
        discounts.splice(discounts.indexOf('amount'), 1);
      }
    }
    return { mode: discounts[0] || null, values };
  }

  // Round up without floating point: orders × cents can reach 10^14.
  function ceilDiv(numerator, denominator) {
    const remainder = numerator % denominator;
    return (numerator - remainder) / denominator + (remainder ? 1 : 0);
  }

  // The break-even itself. margin = order value − cost; after = margin −
  // discount. Extra orders needed = margin ÷ (margin − discount) − 1,
  // computed as the equal and more precise discount ÷ after. Orders round
  // up: 119.05 orders cannot be sold, and 119 would fall short.
  function breakEven(priceCents, costCents, discountCents, orders) {
    const marginCents = priceCents - costCents;
    const afterCents = marginCents - discountCents;
    const result = { marginCents, discountCents, afterCents, status: 'ok', extraPercent: null, ratio: null, orders: null };
    if (marginCents <= 0) result.status = 'no-margin';
    else if (afterCents <= 0) result.status = 'loss';
    else {
      if (discountCents === 0) result.status = 'none';
      result.ratio = marginCents / afterCents;
      result.extraPercent = discountCents * 100 / afterCents;
      if (orders) {
        const targetCents = orders * marginCents;
        const needed = ceilDiv(targetCents, afterCents);
        result.orders = {
          baseline: orders, targetCents, needed, extra: needed - orders,
          reachedCents: needed * afterCents,
          // What one order fewer would make: short of the target unless it divides exactly.
          shortCents: (needed - 1) * afterCents,
          divides: targetCents % afterCents === 0
        };
      }
    }
    return result;
  }

  // Round "$ off" steps for the comparison: ladder amounts from about 3% to
  // 35% of the order value, at most six, spread evenly when more qualify.
  function amountSteps(priceCents) {
    const fits = AMOUNT_LADDER.filter((cents) => cents * 100 >= priceCents * 3 && cents * 100 <= priceCents * 35 && cents < priceCents);
    if (fits.length <= 6) return fits;
    return [0, 1, 2, 3, 4, 5].map((i) => fits[Math.round(i * (fits.length - 1) / 5)]);
  }

  const shareOfPrice = (cents, priceCents) => formatPercent(cents * 100 / priceCents);

  // The same order value and cost at common discounts, plus the visitor's
  // own. In free-shipping mode the shipping row sits among the percentages it
  // costs the same as, which answers "is free shipping cheaper than 10% off?".
  function comparisonRows(input) {
    const { mode, priceCents: P, costCents: C, discountCents: D, percentBps, orders } = input;
    const percentOffers = STEPS_BPS.map((bps) => ({ kind: 'percent', bps, cents: percentOf(P, bps) }));
    let offers;
    if (mode === 'percent') {
      offers = percentOffers.some((o) => o.bps === percentBps) ? percentOffers : percentOffers.concat({ kind: 'percent', bps: percentBps, cents: D });
      offers.forEach((o) => { o.current = o.bps === percentBps; });
      offers.sort((a, b) => a.bps - b.bps);
    } else if (mode === 'amount') {
      offers = amountSteps(P).map((cents) => ({ kind: 'amount', cents }));
      if (!offers.some((o) => o.cents === D)) offers.push({ kind: 'amount', cents: D });
      offers.forEach((o) => { o.current = o.cents === D; });
      offers.sort((a, b) => a.cents - b.cents);
    } else {
      offers = percentOffers.concat({ kind: 'shipping', cents: D, current: true });
      offers.sort((a, b) => a.cents - b.cents || (a.kind === 'shipping') - (b.kind === 'shipping'));
    }
    return offers.map((offer) => {
      const r = breakEven(P, C, offer.cents, orders);
      const loses = r.status === 'loss';
      return {
        label: offer.kind === 'percent' ? formatBps(offer.bps) : offer.kind === 'amount' ? formatMoney(offer.cents) : 'Free shipping',
        note: offer.kind === 'percent' ? `${formatMoney(offer.cents)} off` : offer.kind === 'amount' ? `${shareOfPrice(offer.cents, P)} of the order` : `${formatMoney(offer.cents)} shipping you stop charging`,
        current: Boolean(offer.current),
        loses,
        profit: formatMoney(r.afterCents),
        extra: loses ? 'Never' : formatPercent(r.extraPercent),
        detail: loses ? (r.afterCents === 0 ? 'each order makes $0' : `each order loses ${formatMoney(-r.afterCents)}`) : r.orders ? count(r.orders.needed, 'order') : ''
      };
    });
  }

  // Everything the page shows, as plain strings. The server renders the
  // default example from this and the browser re-renders it as the visitor
  // types, so both always say the same thing.
  //   answer  the headline: "At 10% off, you need | 19% more orders | to make
  //           the same profit." and "That’s 120 orders instead of 100."
  //   extra   the same answer without the offer, as the /tools card quotes
  //           it under its own "At 10% off" (growth-hub.cjs).
  //   profit  "$30 → $25.20" and the math, one [label, text] line per step.
  function describe(values) {
    const input = readInputs(values);
    const view = { mode: input.mode, status: 'invalid', errors: input.errors, answer: null, extra: null, profit: null, problem: '', rows: [], compareNote: '', announcement: '' };
    if (!input.valid) {
      const key = ['price', 'cost', input.mode].find((name) => input.errors[name]);
      const error = key ? input.errors[key] : null;
      const blank = !error || error.kind === 'blank';
      view.problem = `${blank ? 'Enter' : 'Check'} ${NOUNS[key || 'price'][blank ? 0 : 1]} to see your break-even.`;
      view.announcement = blank ? view.problem : `${view.problem} ${error.message}`;
      return view;
    }
    const { mode, priceCents: P, costCents: C, discountCents: D, orders } = input;
    const r = breakEven(P, C, D, orders);
    const money = formatMoney;
    const M = r.marginCents;
    const A = r.afterCents;
    view.status = r.status;
    const nowLine = ['Profit per order now', `${money(P)} order value ${MINUS} ${money(C)} cost = ${money(M)}`];
    if (r.status === 'no-margin') {
      view.profit = { before: money(M), after: '', lines: [nowLine] };
      view.problem = `At full price each order ${M === 0 ? 'makes $0' : `loses ${money(-M)}`}, so there’s no profit for a discount to protect. Check your order value and cost.`;
      view.announcement = view.problem;
      return view;
    }
    // No-break spaces keep "10% off" and "19% more orders" whole when the
    // headline wraps.
    const offer = mode === 'percent' ? `At ${formatBps(input.percentBps)}${NBSP}off` : mode === 'amount' ? `At ${money(D)}${NBSP}off` : 'With free shipping';
    const discountText = mode === 'percent'
      ? `${formatBps(input.percentBps)} × ${money(P)} = ${money(D)}`
      : mode === 'amount' ? `${money(D)} off` : `You stop charging ${money(D)} for shipping`;
    const lines = [nowLine, ['Discount', discountText], ['Profit per order after', `${money(M)} ${MINUS} ${money(D)} = ${money(A)}`]];
    view.profit = { before: money(M), after: money(A), lines };
    view.rows = comparisonRows(input);
    view.compareNote = `Same ${money(P)} order value and ${money(C)} cost.${orders ? ` Orders are what you’d need to match ${count(orders, 'full-price order')}.` : ''}`;
    if (r.status === 'loss') {
      view.problem = `${offer}, ${A === 0 ? 'each order makes $0' : `every order loses ${money(-A)}`}. No number of extra orders makes the money back.`;
      view.announcement = view.problem;
      return view;
    }
    const percent = formatPercent(r.extraPercent);
    lines.push(['More orders needed', `${money(M)} ÷ ${money(A)} = ${formatRatio(r.extraPercent)}, so ${percent} more`]);
    let ordersLine = '';
    if (r.orders) {
      const o = r.orders;
      const target = `${group(String(o.baseline))} × ${money(M)} = ${money(o.targetCents)}.`;
      // Whole orders from whole cents: one order fewer than the answer falls
      // short by the cents shown, so the page never rounds a float.
      lines.push(['In orders', o.divides
        ? `${target} ${money(o.targetCents)} ÷ ${money(A)} = ${count(o.needed, 'order')} exactly.`
        : `${target} ${count(o.needed - 1, 'order')} ${o.needed - 1 === 1 ? 'makes' : 'make'} ${money(o.shortCents)}, so you need ${group(String(o.needed))}.`]);
      ordersLine = o.needed === o.baseline ? `That’s the same ${count(o.needed, 'order')}.` : `That’s ${count(o.needed, 'order')} instead of ${group(String(o.baseline))}.`;
    }
    view.answer = { lead: `${offer}, you need`, key: `${percent}${NBSP}more${NBSP}orders`, tail: 'to make the same profit.', orders: ordersLine };
    view.extra = { lead: 'You need', value: percent, tail: 'more orders to make the same profit.' };
    view.announcement = `${view.answer.lead} ${view.answer.key} ${view.answer.tail}${ordersLine ? ` ${ordersLine}` : ''} Profit per order: ${money(M)} now, ${money(A)} after the discount.`;
    return view;
  }

  const escapeHTML = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // One markup source for the comparison rows, used by the server render and
  // by the browser, which rebuilds only this table body on each input.
  function rowsMarkup(rows) {
    return rows.map((row) => {
      const classes = [row.current && 'is-current', row.loses && 'is-loss'].filter(Boolean).join(' ');
      return `<tr${classes ? ` class="${classes}"` : ''}${row.current ? ' aria-current="true"' : ''}><th scope="row"><span class="dbc-offer">${escapeHTML(row.label)}</span>${row.current ? ' <span class="dbc-yours">Yours</span>' : ''}<small>${escapeHTML(row.note)}</small></th><td><strong>${escapeHTML(row.profit)}</strong></td><td><strong>${escapeHTML(row.extra)}</strong>${row.detail ? `<small>${escapeHTML(row.detail)}</small>` : ''}</td></tr>`;
    }).join('');
  }

  function init(root) {
    if (!root || root.dataset.dbcReady === 'true') return;
    const doc = root.ownerDocument;
    const win = doc.defaultView;
    const form = root.querySelector('[data-dbc-form]');
    if (!form) return;
    root.dataset.dbcReady = 'true';
    const find = (name) => root.querySelector(`[data-dbc-${name}]`);
    const input = (key) => root.querySelector(`[data-dbc-input="${key}"]`);
    const radio = (mode) => form.querySelector(`input[name="dbc-kind"][value="${mode}"]`);
    const set = (name, value) => { const node = find(name); if (node) node.textContent = value; };
    const hide = (name, hidden) => { const node = find(name); if (node) node.hidden = hidden; };
    // A blank box only reads as a mistake once the visitor has left it:
    // clearing a box to retype must not flash an error.
    const left = new Set();
    let usageReported = false;
    let typed = false;
    let settleTimer;
    let announcementTimer;

    const mode = () => {
      const checked = form.querySelector('input[name="dbc-kind"]:checked');
      return checked && MODES.includes(checked.value) ? checked.value : 'percent';
    };
    const values = () => {
      const current = { mode: mode() };
      for (const key of FIELDS) current[key] = input(key) ? input(key).value : '';
      return current;
    };

    function markUsed() {
      if (usageReported) return;
      usageReported = true;
      doc.dispatchEvent(new win.CustomEvent('myuser:tool-event', { detail: { name: 'discount_calculator_used' } }));
    }

    // Box messages. Settled, each box says what it needs; mid-edit a message
    // may clear or change, but a new one waits until typing pauses.
    function applyErrors(view, settled) {
      for (const key of FIELDS) {
        const box = input(key);
        if (!box) continue;
        const error = view.errors[key];
        const active = !MODES.includes(key) || key === view.mode;
        const wanted = Boolean(error && active && (error.kind !== 'blank' || left.has(key)));
        const shown = wanted && (settled || box.getAttribute('aria-invalid') === 'true');
        box.setAttribute('aria-invalid', String(shown));
        const field = box.closest('.dbc-field');
        if (field) field.classList.toggle('is-invalid', shown);
        set(`error-${key}`, shown ? error.message : '');
        hide(`error-${key}`, !shown);
      }
    }

    function apply(view, settled) {
      root.dataset.dbcStatus = view.status;
      root.dataset.dbcMode = view.mode;
      root.querySelectorAll('[data-dbc-mode-field]').forEach((node) => { node.hidden = node.dataset.dbcModeField !== view.mode; });
      applyErrors(view, settled);
      hide('answer', !view.answer);
      if (view.answer) for (const part of ['lead', 'key', 'tail', 'orders']) set(`answer-${part}`, view.answer[part]);
      hide('answer-orders', !(view.answer && view.answer.orders));
      set('problem', view.problem);
      hide('problem', !view.problem);
      hide('profit', !view.profit);
      if (view.profit) {
        set('profit-before', view.profit.before);
        set('profit-after', view.profit.after);
        hide('profit-to', !view.profit.after);
        root.querySelectorAll('[data-dbc-line]').forEach((row, index) => {
          const line = view.profit.lines[index];
          row.hidden = !line;
          if (!line) return;
          row.querySelector('dt').textContent = line[0];
          row.querySelector('dd').textContent = line[1];
        });
      }
      hide('compare', !view.rows.length);
      const body = find('rows');
      if (body && view.rows.length) body.innerHTML = rowsMarkup(view.rows);
      set('compare-note', view.compareNote);
    }

    function announce(text) {
      win.clearTimeout(announcementTimer);
      // One short summary, not a reading of every number or every radio passed.
      announcementTimer = win.setTimeout(() => set('announcement', text), 300);
    }

    // A result that works shows as soon as it's typed. One that doesn't (a
    // blank box, the "1" on the way to "120", a loss) waits until typing
    // pauses or the box is left, with the last result dimmed meanwhile, so
    // no keystroke flashes an error or collapses the page.
    function update(settled) {
      const view = describe(values());
      const works = (view.status === 'ok' || view.status === 'none') && !Object.keys(view.errors).length;
      win.clearTimeout(settleTimer);
      if (settled || works) apply(view, settled);
      else applyErrors(view, false);
      root.dataset.dbcPending = String(!settled && !works);
      if (!settled) {
        settleTimer = win.setTimeout(() => update(true), SETTLE_MS);
      } else if (typed) {
        typed = false;
        announce(view.announcement);
      }
    }

    form.addEventListener('submit', (event) => event.preventDefault());
    form.addEventListener('input', (event) => {
      if (!event.target.dataset || !event.target.dataset.dbcInput) return;
      markUsed();
      typed = true;
      update(false);
    });
    form.addEventListener('change', (event) => {
      if (event.target.name !== 'dbc-kind') return;
      markUsed();
      typed = true;
      update(true);
    });
    form.addEventListener('focusout', (event) => {
      const box = event.target;
      const key = box.dataset && box.dataset.dbcInput;
      if (!key) return;
      const tidy = tidyValue(key, box.value);
      if (tidy !== box.value) box.value = tidy;
      left.add(key);
      update(true);
    });
    // Browsers may restore typed values when the visitor comes back.
    win.addEventListener('pageshow', () => update(true));
    win.addEventListener('pagehide', () => {
      win.clearTimeout(settleTimer);
      win.clearTimeout(announcementTimer);
    });

    // A link may carry numbers to start from (?price=60&cost=35&percent=15).
    // They fill the boxes; the address is never written back.
    const query = readQuery(win.location ? win.location.search : '', values());
    for (const key of Object.keys(query.values)) {
      const box = input(key);
      if (box) box.value = query.values[key];
    }
    if (query.mode && radio(query.mode)) radio(query.mode).checked = true;
    update(true);
  }

  return Object.freeze({
    DEFAULTS, MODES, MAX_CENTS, MAX_ORDERS, SETTLE_MS,
    parseDecimal, readInputs, readQuery, tidyValue, breakEven, comparisonRows, amountSteps, describe, rowsMarkup,
    formatMoney, formatBps, formatPercent, formatRatio, init
  });
});
